import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { CheckCircle2 } from 'lucide-react';
import { padChatbotInfoFields } from '@workspace/types';
import type { AiApiKeyStatus, ChatbotAiCatalog, NoragBotConfigStatus } from '../../types/chatbot.types';
import { InfoFieldsEditor } from './InfoFieldsEditor';

function defaultModelIdForProvider(
  provider: 'openai' | 'anthropic' | 'openrouter' | 'local',
  catalog?: ChatbotAiCatalog | null,
): string {
  if (provider === 'anthropic') {
    return 'claude-sonnet-5';
  }
  if (provider === 'openrouter') {
    return catalog?.openrouter[0]?.id ?? 'z-ai/glm-5.3';
  }
  if (provider === 'local') {
    return catalog?.local[0]?.id ?? '';
  }
  return 'gpt-5.6-terra';
}

function KeyStatusBadge({ status }: { status?: AiApiKeyStatus }) {
  if (!status) {
    return (
      <Badge variant='outline' className='text-xs'>
        Sin datos
      </Badge>
    );
  }

  if (status.storedOnBot) {
    return (
      <Badge variant='default' className='text-xs gap-1'>
        <CheckCircle2 className='h-3 w-3' />
        Key guardada en el bot
      </Badge>
    );
  }

  if (status.configured && status.source === 'tenant') {
    return (
      <Badge variant='secondary' className='text-xs'>
        Usa key del tenant
      </Badge>
    );
  }

  if (status.configured && status.source === 'environment') {
    return (
      <Badge variant='outline' className='text-xs'>
        Entorno (dev)
      </Badge>
    );
  }

  return (
    <Badge variant='outline' className='text-xs'>
      No configurada
    </Badge>
  );
}

const botConfigSchema = z
  .object({
    name: z.string().min(1, 'Nombre requerido'),
    description: z.string().optional(),
    kind: z.enum(['STANDARD', 'CONCIERGE']),
    provider: z.enum(['openai', 'anthropic', 'openrouter', 'local']),
    modelId: z.string().optional(),
    systemPrompt: z.string().optional(),
    openaiApiKey: z.string().optional(),
    anthropicApiKey: z.string().optional(),
    clearOpenaiApiKey: z.boolean().optional(),
    clearAnthropicApiKey: z.boolean().optional(),
    temperature: z.string().optional(),
    maxOutputTokens: z.string().optional(),
    sttModelId: z.string().optional(),
    historyMessageLimit: z.string().optional(),
    historyMessageMaxChars: z.string().optional(),
    conversationMode: z.enum(['SINGLE', 'MULTIPLE']),
    iconUrl: z.string().optional(),
    displayOrder: z.string().optional(),
    noragEnabled: z.boolean().optional(),
    noragEmpresaId: z.string().optional(),
    noragBotId: z.string().optional(),
    noragApiUrl: z.string().optional(),
    noragPathPrefix: z.string().optional(),
    noragBotToken: z.string().optional(),
    clearNoragBotToken: z.boolean().optional(),
    welcomeText: z.string().optional(),
    webMandatory: z.boolean().optional(),
    idleMinutes: z.string().optional(),
    pendingIdleMinutes: z.string().optional(),
    escapeKeywords: z.string().optional(),
    classifyEnabled: z.boolean().optional(),
    topicShiftEnabled: z.boolean().optional(),
    menuLabel: z.string().optional(),
    menuDescription: z.string().optional(),
    isRoutable: z.boolean().optional(),
    botInfoFieldsEnabled: z.boolean().optional(),
    userInfoFieldsEnabled: z.boolean().optional(),
    infoFields: z
      .array(z.object({ label: z.string().max(64), value: z.string().max(500) }))
      .max(5)
      .optional(),
    operatorIdleResumeMinutes: z.string().optional(),
    operatorTakeoverNotice: z.string().max(500).optional(),
    operatorResumeNotice: z.string().max(500).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.kind === 'CONCIERGE') {
      if (!data.welcomeText?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'El conserje requiere un texto de bienvenida',
          path: ['welcomeText'],
        });
      }
      return;
    }
    if (!data.modelId?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Modelo requerido',
        path: ['modelId'],
      });
    }
    if (!data.systemPrompt?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'System prompt requerido',
        path: ['systemPrompt'],
      });
    }
  });

export type BotConfigurationFormData = z.infer<typeof botConfigSchema>;

type BotConfigurationFormProps = {
  mode: 'create' | 'edit';
  initialValues?: Partial<BotConfigurationFormData>;
  catalog?: ChatbotAiCatalog | null;
  aiReady?: boolean;
  openaiApiKeyStatus?: AiApiKeyStatus;
  anthropicApiKeyStatus?: AiApiKeyStatus;
  noragStatus?: NoragBotConfigStatus;
  lastAuthError?: string | null;
  showDisplayOrder?: boolean;
  saving?: boolean;
  onSubmit: (data: BotConfigurationFormData) => Promise<void>;
  onCancel?: () => void;
  existingConcierge?: { botId: string; name: string } | null;
  editingBotId?: string | null;
  showInstanceCatalogFields?: boolean;
  classifyNeedsModel?: boolean;
};

const DEFAULT_VALUES: BotConfigurationFormData = {
  name: '',
  description: '',
  kind: 'STANDARD',
  provider: 'openai',
  modelId: 'gpt-4o-mini',
  systemPrompt: '',
  openaiApiKey: '',
  anthropicApiKey: '',
  clearOpenaiApiKey: false,
  clearAnthropicApiKey: false,
  temperature: '0.7',
  maxOutputTokens: '4096',
  sttModelId: 'whisper-1',
  historyMessageLimit: '40',
  historyMessageMaxChars: '8000',
  conversationMode: 'SINGLE',
  iconUrl: '',
  displayOrder: '0',
  noragEnabled: false,
  noragEmpresaId: '',
  noragBotId: '',
  noragApiUrl: '',
  noragPathPrefix: '',
  noragBotToken: '',
  clearNoragBotToken: false,
  welcomeText: '',
  webMandatory: false,
  idleMinutes: '30',
  pendingIdleMinutes: '240',
  escapeKeywords: '',
  classifyEnabled: true,
  topicShiftEnabled: false,
  menuLabel: '',
  menuDescription: '',
  isRoutable: true,
  botInfoFieldsEnabled: false,
  userInfoFieldsEnabled: false,
  infoFields: padChatbotInfoFields([]),
  operatorIdleResumeMinutes: '30',
  operatorTakeoverNotice: '',
  operatorResumeNotice: '',
};

export function BotConfigurationForm({
  mode,
  initialValues,
  catalog,
  aiReady,
  openaiApiKeyStatus,
  noragStatus,
  lastAuthError,
  showDisplayOrder = false,
  saving = false,
  onSubmit,
  onCancel,
  existingConcierge = null,
  editingBotId = null,
  showInstanceCatalogFields = false,
  classifyNeedsModel = false,
}: BotConfigurationFormProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [useCustomModel, setUseCustomModel] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BotConfigurationFormData>({
    resolver: zodResolver(botConfigSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedModelId = watch('modelId');
  const watchedProvider = watch('provider');
  const watchedKind = watch('kind');
  const isConcierge = watchedKind === 'CONCIERGE';
  const conciergeLocked =
    Boolean(existingConcierge) && existingConcierge?.botId !== (editingBotId ?? undefined);
  const botInfoFieldsEnabled = Boolean(watch('botInfoFieldsEnabled'));
  const userInfoFieldsEnabled = Boolean(watch('userInfoFieldsEnabled'));
  const infoFields = watch('infoFields') ?? padChatbotInfoFields([]);

  useEffect(() => {
    reset({
      ...DEFAULT_VALUES,
      ...initialValues,
      clearOpenaiApiKey: false,
      clearAnthropicApiKey: false,
      openaiApiKey: '',
      anthropicApiKey: '',
      noragBotToken: '',
    });
    const providerForPreset = initialValues?.provider ?? DEFAULT_VALUES.provider;
    const presetMatch =
      providerForPreset === 'openrouter'
        ? catalog?.openrouter.some(model => model.id === initialValues?.modelId)
        : providerForPreset === 'local'
          ? catalog?.local.some(model => model.id === initialValues?.modelId)
          : catalog?.models.some(
              model =>
                model.modelId === initialValues?.modelId &&
                model.provider === initialValues?.provider,
            );
    setUseCustomModel(Boolean(initialValues?.modelId && !presetMatch));
  }, [initialValues, reset, catalog]);

  const modelPresets = useMemo(() => {
    if (watchedProvider === 'openrouter') {
      return (
        catalog?.openrouter.map(model => ({
          id: model.id,
          label: model.label,
          modelId: model.id,
          provider: 'openrouter' as const,
          description: model.vendor ?? 'OpenRouter',
          supportsVision: true,
        })) ?? []
      );
    }
    if (watchedProvider === 'local') {
      return (
        catalog?.local.map(model => ({
          id: model.id,
          label: model.label,
          modelId: model.id,
          provider: 'local' as const,
          description: 'Ollama / LM Studio',
          supportsVision: false,
        })) ?? []
      );
    }
    return catalog?.models.filter(model => model.provider === watchedProvider) ?? [];
  }, [catalog, watchedProvider]);

  const providerUnavailableReason = useMemo(() => {
    if (watchedProvider === 'openrouter' && catalog && !catalog.openrouterConfigured) {
      return 'Configurá la API key OpenRouter en keys del tenant';
    }
    if (watchedProvider === 'openrouter' && catalog && catalog.openrouter.length === 0) {
      return 'Habilitá al menos un modelo OpenRouter en keys del tenant';
    }
    if (watchedProvider === 'local' && catalog && !catalog.localConfigured) {
      return 'Configurá la URL local en keys del tenant';
    }
    if (watchedProvider === 'local' && catalog && catalog.local.length === 0) {
      return 'Habilitá al menos un modelo local en keys del tenant';
    }
    return null;
  }, [catalog, watchedProvider]);
  const sttPresets = catalog?.sttModels ?? [{ id: 'whisper-1', label: 'Whisper 1' }];
  const promptTemplates = catalog?.promptTemplates ?? [];

  const selectedModel = useMemo(
    () => modelPresets.find(model => model.modelId === watchedModelId),
    [modelPresets, watchedModelId],
  );

  const applyTemplate = (templateId: string) => {
    const template = promptTemplates.find(item => item.id === templateId);
    if (!template) {
      return;
    }
    setValue('systemPrompt', template.systemPrompt);
    const suggestedPreset = catalog?.models.find(model => model.modelId === template.suggestedModelId);
    if (suggestedPreset) {
      setValue('provider', suggestedPreset.provider);
      setValue('modelId', suggestedPreset.modelId);
      setUseCustomModel(false);
    } else {
      setValue('modelId', template.suggestedModelId);
      setUseCustomModel(!catalog?.models.some(model => model.modelId === template.suggestedModelId));
    }
    setSelectedTemplateId(templateId);
  };

  const handleProviderChange = (
    provider: 'openai' | 'anthropic' | 'openrouter' | 'local',
  ) => {
    setValue('provider', provider);
    const defaultModelId = defaultModelIdForProvider(provider, catalog);
    setValue('modelId', defaultModelId);
    setUseCustomModel(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant={aiReady ? 'default' : 'secondary'}>
          {aiReady ? 'IA lista' : 'IA pendiente de configuración'}
        </Badge>
        {lastAuthError && (
          <Badge variant='destructive' className='max-w-full truncate'>
            {lastAuthError}
          </Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tipo de bot</CardTitle>
          <CardDescription>
            El conserje rutea a los asistentes de la misma instancia. Un solo conserje por tenant.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div className='flex flex-wrap gap-4'>
            <label className='flex items-center gap-2 text-sm'>
              <input type='radio' value='STANDARD' {...register('kind')} />
              Asistente
            </label>
            <label className='flex items-center gap-2 text-sm'>
              <input
                type='radio'
                value='CONCIERGE'
                {...register('kind')}
                disabled={conciergeLocked}
              />
              Conserje
            </label>
          </div>
          {conciergeLocked && existingConcierge && (
            <p className='text-sm text-muted-foreground'>
              Este tenant ya tiene un conserje ({existingConcierge.name}).
            </p>
          )}
          {isConcierge && (
            <p className='text-sm text-muted-foreground'>
              Atá WhatsApp, Telegram, Helpdesk o Chatwoot al conserje para ruteo. Los demás bots
              siguen como línea dedicada.
            </p>
          )}
        </CardContent>
      </Card>

      {isConcierge && (
        <Card>
          <CardHeader>
            <CardTitle>Conserje</CardTitle>
            <CardDescription>Bienvenida, web obligatorio, timeouts y clasificador.</CardDescription>
          </CardHeader>
          <CardContent className='grid gap-4 md:grid-cols-2'>
            <div className='md:col-span-2'>
              <Label htmlFor='welcomeText'>Texto de bienvenida *</Label>
              <Textarea id='welcomeText' rows={4} {...register('welcomeText')} />
              {errors.welcomeText && (
                <p className='text-sm text-red-500'>{errors.welcomeText.message}</p>
              )}
            </div>
            <label className='flex items-center gap-2 text-sm md:col-span-2'>
              <input type='checkbox' {...register('webMandatory')} />
              Web obligatorio (el usuario no elige especialista)
            </label>
            <div>
              <Label htmlFor='idleMinutes'>Minutos de inactividad (5–1440)</Label>
              <Input id='idleMinutes' type='number' min={5} max={1440} {...register('idleMinutes')} />
            </div>
            <div>
              <Label htmlFor='pendingIdleMinutes'>Inactividad con trámite pendiente</Label>
              <Input
                id='pendingIdleMinutes'
                type='number'
                min={5}
                max={1440}
                {...register('pendingIdleMinutes')}
              />
            </div>
            <div className='md:col-span-2'>
              <Label htmlFor='escapeKeywords'>Keywords extra de menú (separadas por coma)</Label>
              <Input id='escapeKeywords' {...register('escapeKeywords')} placeholder='inicio, home' />
            </div>
            <label className='flex items-center gap-2 text-sm'>
              <input
                type='checkbox'
                {...register('classifyEnabled')}
                disabled={classifyNeedsModel}
              />
              Clasificar texto libre con IA
            </label>
            {classifyNeedsModel && (
              <p className='text-xs text-muted-foreground md:col-span-2'>
                Sin modelo y API key el clasificador queda apagado y se muestra el menú.
              </p>
            )}
            <label className='flex items-center gap-2 text-sm md:col-span-2'>
              <input type='checkbox' {...register('topicShiftEnabled')} />
              Detectar cambio de tema en el especialista (riesgo de interrupciones; default off)
            </label>
          </CardContent>
        </Card>
      )}

      {showInstanceCatalogFields && !isConcierge && (
        <Card>
          <CardHeader>
            <CardTitle>Acceso desde el conserje</CardTitle>
            <CardDescription>
              Los bots pueden atenderse por canal directo (WhatsApp, Telegram, web) sin aparecer en el conserje.
              Desactivá el ruteo si solo debe accederse por el canal configurado.
            </CardDescription>
          </CardHeader>
          <CardContent className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label htmlFor='menuLabel'>Etiqueta en menú (máx. 24)</Label>
              <Input id='menuLabel' maxLength={24} {...register('menuLabel')} />
            </div>
            <div>
              <Label htmlFor='menuDescription'>Descripción corta (máx. 72)</Label>
              <Input id='menuDescription' maxLength={72} {...register('menuDescription')} />
            </div>
            <div className='md:col-span-2 space-y-2'>
              <label className='flex items-center gap-2 text-sm'>
                <input type='checkbox' {...register('isRoutable')} />
                Incluir en el menú y ruteo del conserje
              </label>
              <p className='text-xs text-muted-foreground'>
                Si está desactivado, el bot no aparece en el menú del conserje ni recibe derivaciones desde allí.
                Sigue disponible por su canal directo.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Identidad</CardTitle>
          <CardDescription>Nombre visible y modo de conversación del bot.</CardDescription>
        </CardHeader>
        <CardContent className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label htmlFor='botName'>Nombre *</Label>
            <Input id='botName' {...register('name')} />
            {errors.name && <p className='text-sm text-red-500'>{errors.name.message}</p>}
          </div>
          {!isConcierge && (
          <div>
            <Label htmlFor='conversationMode'>Modo de conversación</Label>
            <select
              id='conversationMode'
              {...register('conversationMode')}
              className='w-full border border-input bg-background rounded-md p-2'
            >
              <option value='SINGLE'>Única conversación</option>
              <option value='MULTIPLE'>Múltiples conversaciones</option>
            </select>
          </div>
          )}
          <div className='md:col-span-2'>
            <Label htmlFor='botDescription'>Descripción</Label>
            <Textarea id='botDescription' rows={2} {...register('description')} />
          </div>
          <div>
            <Label htmlFor='iconUrl'>URL del icono</Label>
            <Input id='iconUrl' {...register('iconUrl')} placeholder='https://...' />
          </div>
          {showDisplayOrder && (
            <div>
              <Label htmlFor='displayOrder'>Orden en instancia</Label>
              <Input id='displayOrder' type='number' {...register('displayOrder')} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modelo y proveedor</CardTitle>
          <CardDescription>
            OpenAI, Anthropic, OpenRouter o LLM local (Ollama / LM Studio).
          </CardDescription>
        </CardHeader>
        <CardContent className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label htmlFor='provider'>Proveedor</Label>
            <select
              id='provider'
              value={watchedProvider}
              onChange={event =>
                handleProviderChange(
                  event.target.value as 'openai' | 'anthropic' | 'openrouter' | 'local',
                )
              }
              className='w-full border border-input bg-background rounded-md p-2'
            >
              <option value='openai'>OpenAI</option>
              <option value='anthropic'>Anthropic</option>
              <option
                value='openrouter'
                disabled={Boolean(catalog && !catalog.openrouterConfigured)}
              >
                OpenRouter
              </option>
              <option value='local' disabled={Boolean(catalog && !catalog.localConfigured)}>
                LLM local (Ollama / LM Studio)
              </option>
            </select>
            {providerUnavailableReason && (
              <p className='text-xs text-muted-foreground mt-1'>{providerUnavailableReason}</p>
            )}
          </div>
          <div>
            <Label>Modelo</Label>
            {!useCustomModel ? (
              <select
                className='w-full border border-input bg-background rounded-md p-2'
                value={watchedModelId}
                onChange={event => {
                  const value = event.target.value;
                  if (value === '__custom__') {
                    setUseCustomModel(true);
                    return;
                  }
                  setValue('modelId', value);
                }}
              >
                {modelPresets.map(model => (
                  <option key={model.id} value={model.modelId}>
                    {model.label}
                    {model.description ? ` — ${model.description}` : ''}
                  </option>
                ))}
                {(watchedProvider === 'openai' || watchedProvider === 'anthropic') && (
                  <option value='__custom__'>Otro modelo (manual)</option>
                )}
              </select>
            ) : (
              <div className='space-y-2'>
                <Input
                  {...register('modelId')}
                  placeholder={
                    watchedProvider === 'anthropic' ? 'claude-sonnet-5' : 'gpt-5.6-terra'
                  }
                />
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => setUseCustomModel(false)}
                >
                  Usar lista de modelos
                </Button>
              </div>
            )}
            {errors.modelId && <p className='text-sm text-red-500'>{errors.modelId.message}</p>}
            {selectedModel && (
              <p className='text-xs text-muted-foreground mt-1'>
                Visión: {selectedModel.supportsVision ? 'sí' : 'no'}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor='sttModelId'>Modelo STT (audio)</Label>
            <select
              id='sttModelId'
              {...register('sttModelId')}
              className='w-full border border-input bg-background rounded-md p-2'
            >
              {sttPresets.map(model => (
                <option key={model.id} value={model.id}>
                  {model.label}
                </option>
              ))}
            </select>
            <p className='text-xs text-muted-foreground mt-1'>
              Whisper usa la key OpenAI del bot o del tenant.
            </p>
          </div>
        </CardContent>
      </Card>

      {!isConcierge && (
      <>
      <div className='flex flex-wrap items-center gap-2 rounded-lg border border-border px-4 py-3 text-sm'>
        <span className='font-medium text-foreground'>Clave de IA</span>
        <KeyStatusBadge status={openaiApiKeyStatus} />
        <span className='text-muted-foreground'>
          {openaiApiKeyStatus?.storedOnBot
            ? 'Este bot todavía usa una clave propia. El script de migración la pasa al cliente.'
            : 'Se usa la clave del cliente. Se carga una sola vez, en su ficha.'}
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>System prompt</CardTitle>
          <CardDescription>
            Reemplaza el chatflow de Flowise. Define rol, límites y estilo del bot.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {promptTemplates.length > 0 && (
            <div className='flex flex-wrap items-end gap-2'>
              <div className='grow'>
                <Label htmlFor='promptTemplate'>Plantilla migrada desde Flowise</Label>
                <select
                  id='promptTemplate'
                  className='w-full border border-input bg-background rounded-md p-2'
                  value={selectedTemplateId}
                  onChange={event => {
                    const value = event.target.value;
                    setSelectedTemplateId(value);
                    if (value) {
                      applyTemplate(value);
                    }
                  }}
                >
                  <option value=''>Seleccionar plantilla...</option>
                  {promptTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} — {template.description}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type='button'
                variant='outline'
                disabled={!selectedTemplateId}
                onClick={() => selectedTemplateId && applyTemplate(selectedTemplateId)}
              >
                Aplicar plantilla
              </Button>
            </div>
          )}
          <div>
            <Label htmlFor='systemPrompt'>Prompt del sistema *</Label>
            <Textarea id='systemPrompt' rows={12} {...register('systemPrompt')} />
            {errors.systemPrompt && (
              <p className='text-sm text-red-500'>{errors.systemPrompt.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Memoria de sesión</CardTitle>
          <CardDescription>
            Historial enviado al modelo en cada turno (PostgreSQL). El hilo de conversación usa
            aiThreadId internamente.
          </CardDescription>
        </CardHeader>
        <CardContent className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label htmlFor='historyMessageLimit'>Mensajes en contexto</Label>
            <Input
              id='historyMessageLimit'
              type='number'
              min={1}
              max={200}
              {...register('historyMessageLimit')}
            />
            <p className='text-xs text-muted-foreground mt-1'>Default: 40 (máx. 200)</p>
          </div>
          <div>
            <Label htmlFor='historyMessageMaxChars'>Caracteres por mensaje</Label>
            <Input
              id='historyMessageMaxChars'
              type='number'
              min={256}
              max={32000}
              {...register('historyMessageMaxChars')}
            />
            <p className='text-xs text-muted-foreground mt-1'>Default: 8000</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inbox de operador</CardTitle>
          <CardDescription>
            Takeover humano, timeout de inactividad y avisos al usuario cuando un operador interviene
            o el bot retoma la conversación.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div>
            <Label htmlFor='operatorIdleResumeMinutes'>Minutos de inactividad para retomar el bot</Label>
            <Input
              id='operatorIdleResumeMinutes'
              type='number'
              min={0}
              max={10080}
              {...register('operatorIdleResumeMinutes')}
            />
            <p className='text-xs text-muted-foreground mt-1'>
              0 = solo manual con el botón &quot;Restablecer bot&quot;. Default: 30.
            </p>
          </div>
          <div>
            <Label htmlFor='operatorTakeoverNotice'>Aviso al usuario al tomar la conversación</Label>
            <Textarea
              id='operatorTakeoverNotice'
              rows={2}
              maxLength={500}
              placeholder='Un operador se unió a la conversación.'
              {...register('operatorTakeoverNotice')}
            />
          </div>
          <div>
            <Label htmlFor='operatorResumeNotice'>Aviso al usuario al restablecer el bot</Label>
            <Textarea
              id='operatorResumeNotice'
              rows={2}
              maxLength={500}
              placeholder='El asistente virtual retomó la conversación.'
              {...register('operatorResumeNotice')}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Base de conocimiento NORAG</CardTitle>
          <CardDescription>
            Recuperación semántica de dominios al responder. Con provisioning automático, el bot
            NORAG se crea al guardar si falta credencial.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <label className='flex items-center gap-2 text-sm'>
            <input type='checkbox' {...register('noragEnabled')} />
            Habilitar retrieval NORAG en este bot
          </label>

          {noragStatus && (
            <div className='flex flex-wrap gap-2 text-xs'>
              <Badge variant={noragStatus.configured ? 'default' : 'outline'}>
                {noragStatus.configured ? 'NORAG listo' : 'NORAG incompleto'}
              </Badge>
              {noragStatus.autoProvisionAvailable && (
                <Badge variant='secondary'>Auto-provision disponible</Badge>
              )}
              {noragStatus.storedTokenOnBot && (
                <Badge variant='outline'>Token guardado en el bot</Badge>
              )}
            </div>
          )}

          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label htmlFor='noragEmpresaId'>Empresa NORAG (ID numérico)</Label>
              <Input
                id='noragEmpresaId'
                type='number'
                placeholder='Ej: 1'
                {...register('noragEmpresaId')}
              />
              <p className='text-xs text-muted-foreground mt-1'>
                Opcional si el tenant tiene tenant.config.norag.empresaId
              </p>
            </div>
            <div>
              <Label htmlFor='noragPathPrefix'>Prefijo de path (opcional)</Label>
              <Input id='noragPathPrefix' placeholder='/empresa/acme' {...register('noragPathPrefix')} />
            </div>
            <div>
              <Label htmlFor='noragApiUrl'>URL API NORAG (override)</Label>
              <Input
                id='noragApiUrl'
                placeholder='https://norag.ejemplo.com'
                {...register('noragApiUrl')}
              />
            </div>
            {mode === 'edit' && (
              <div>
                <Label htmlFor='noragBotId'>Bot NORAG (UUID)</Label>
                <Input id='noragBotId' readOnly {...register('noragBotId')} />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor='noragBotToken'>Token bot NORAG (manual)</Label>
            <Input
              id='noragBotToken'
              type='password'
              autoComplete='new-password'
              placeholder={
                noragStatus?.autoProvisionAvailable
                  ? 'Dejar vacío para auto-provision al guardar'
                  : 'Bearer JWT del bot NORAG'
              }
              {...register('noragBotToken')}
            />
            {mode === 'edit' && (
              <label className='flex items-center gap-2 text-sm text-muted-foreground mt-2'>
                <input type='checkbox' {...register('clearNoragBotToken')} />
                Eliminar token guardado (forzar re-provision manual o automático)
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Parámetros de generación</CardTitle>
        </CardHeader>
        <CardContent className='grid gap-4 md:grid-cols-2'>
          <div>
            <Label htmlFor='temperature'>Temperature</Label>
            <Input id='temperature' {...register('temperature')} placeholder='0.7' />
          </div>
          <div>
            <Label htmlFor='maxOutputTokens'>Max output tokens</Label>
            <Input
              id='maxOutputTokens'
              type='number'
              {...register('maxOutputTokens')}
              placeholder='4096'
            />
          </div>
        </CardContent>
      </Card>
      </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Campos de información</CardTitle>
          <CardDescription>
            Se inyectan en cada mensaje si el switch está activo. Apagar no borra los valores.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center justify-between gap-4'>
            <div>
              <Label htmlFor='botInfoFieldsEnabled'>Info del bot</Label>
              <p className='text-xs text-muted-foreground'>
                Avisos operativos para todas las conversaciones (corte, horario, sucursal).
              </p>
            </div>
            <Switch
              id='botInfoFieldsEnabled'
              checked={botInfoFieldsEnabled}
              onCheckedChange={checked => setValue('botInfoFieldsEnabled', checked)}
            />
          </div>
          {botInfoFieldsEnabled && (
            <InfoFieldsEditor
              idPrefix='bot-form'
              fields={infoFields}
              onChange={next => setValue('infoFields', next, { shouldDirty: true })}
            />
          )}
          <div className='flex items-center justify-between gap-4'>
            <div>
              <Label htmlFor='userInfoFieldsEnabled'>Info por persona</Label>
              <p className='text-xs text-muted-foreground'>
                Hasta 5 datos de ese contacto, solo en este bot. Se editan desde la conversación.
              </p>
            </div>
            <Switch
              id='userInfoFieldsEnabled'
              checked={userInfoFieldsEnabled}
              onCheckedChange={checked => setValue('userInfoFieldsEnabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <div className='flex justify-end gap-2'>
        {onCancel && (
          <Button type='button' variant='outline' onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
        )}
        <Button type='submit' disabled={saving}>
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear bot' : 'Guardar configuración'}
        </Button>
      </div>
    </form>
  );
}
